import React, { useState, useEffect } from 'react';
import { BarChart3, Target, TrendingUp, CheckCircle, AlertCircle } from 'lucide-react';
import SmartSuggestions from '../../../../../components/SmartSuggestions';

// Silenciar logs de EPL por defecto (no afecta a Vertex).
// Activar explícitamente con: VITE_EPL_VERBOSE=true
const _eplConsole = globalThis.console;
// eslint-disable-next-line no-shadow
const console = import.meta.env.VITE_EPL_VERBOSE === 'true'
    ? _eplConsole
    : { ..._eplConsole, log: () => {}, info: () => {}, warn: () => {} };

const BMLMeasureStep = ({ 
    onValidateIA, 
    initialData = null, 
    isActive = false,
    sessionData = null // Para obtener hipótesis y MVP del contexto
}) => {
    const [formData, setFormData] = useState({
        metricas_clave: [''],
        metodos_medicion: [''],
        objetivos_especificos: ''
    });
    
    const [validation, setValidation] = useState({
        metricas_clave: { valid: false, message: '' },
        metodos_medicion: { valid: false, message: '' },
        objetivos_especificos: { valid: false, message: '' }
    });

    // 🧠 Función para agregar sugerencias de IA al formulario
    const handleAddSuggestion = (field, suggestion) => {
        const maxItems = field === 'metricas_clave' ? 4 : 5;
        if (formData[field].length < maxItems && !formData[field].includes(suggestion)) {
            setFormData(prev => ({
                ...prev,
                [field]: [...prev[field].filter(item => item && item.trim && item.trim()), suggestion, '']
            }));
        }
    };

    // 💾 Cargar datos iniciales si existen
    useEffect(() => {
        if (initialData && typeof initialData === 'object') {
            console.log('[💾 BMLMeasurePhase] Cargando datos iniciales:', initialData);
            setFormData(prevData => ({
                ...prevData,
                ...initialData
            }));
        }
    }, [initialData]);

    // 🎯 Validación en tiempo real
    useEffect(() => {
        const validMetricas = formData.metricas_clave.filter(m => m && typeof m === 'string' && m.trim().length > 0);
        const validMetodos = formData.metodos_medicion.filter(m => m && typeof m === 'string' && m.trim().length > 0);
        
        setValidation({
            metricas_clave: {
                valid: validMetricas.length >= 2 && validMetricas.length <= 4,
                message: validMetricas.length >= 2 && validMetricas.length <= 4
                    ? '✅ Métricas definidas correctamente'
                    : `❌ Define entre 2-4 métricas clave (actual: ${validMetricas.length})`
            },
            metodos_medicion: {
                valid: validMetodos.length >= 1,
                message: validMetodos.length >= 1
                    ? '✅ Métodos de medición definidos'
                    : '❌ Define al menos 1 método de medición'
            },
            objetivos_especificos: {
                valid: formData.objetivos_especificos.length >= 30,
                message: formData.objetivos_especificos.length >= 30
                    ? '✅ Objetivos específicos definidos'
                    : `❌ Define objetivos medibles (mín. 30 caracteres)`
            }
        });
    }, [formData]);

    const isFormComplete = Object.values(validation).every(v => v.valid);

    const handleListChange = (field, index, value) => {
        setFormData(prev => ({
            ...prev,
            [field]: prev[field].map((item, i) => i === index ? value : item)
        }));
    };

    const addListItem = (field, maxItems) => {
        if (formData[field].length < maxItems) {
            setFormData(prev => ({
                ...prev,
                [field]: [...prev[field], '']
            }));
        }
    };

    const removeListItem = (field, index) => {
        if (formData[field].length > 1) {
            setFormData(prev => ({
                ...prev,
                [field]: prev[field].filter((_, i) => i !== index)
            }));
        }
    };



    const handleValidateIA = () => {
        if (isFormComplete) {
            const measureInput = `
MÉTRICAS CLAVE:
${formData.metricas_clave.filter(m => m.trim()).map((metric, i) => `${i + 1}. ${metric}`).join('\n')}

MÉTODOS DE MEDICIÓN:
${formData.metodos_medicion.filter(m => m.trim()).map((method, i) => `${i + 1}. ${method}`).join('\n')}

OBJETIVOS ESPECÍFICOS:
${formData.objetivos_especificos}
            `.trim();
            
            onValidateIA(measureInput, 'bml_measure', formData);
        }
    };

    if (!isActive) return null;

    return (
        <div className="bg-white rounded-xl shadow-lg border border-green-200 overflow-hidden">
            {/* 🎨 Header con gradiente MEASURE */}
            <div className="bg-gradient-to-r from-green-500 to-green-600 p-6 text-white">
                <div className="flex items-center gap-3">
                    <div className="w-12 h-12 bg-white/20 rounded-lg flex items-center justify-center">
                        <BarChart3 className="w-6 h-6" />
                    </div>
                    <div>
                        <h3 className="text-xl font-bold">MEASURE: Mide el resultado</h3>
                        <p className="text-green-100">Define cómo vas a medir si tu hipótesis es correcta</p>
                    </div>
                </div>
            </div>

            <div className="p-6 space-y-6">
                {/* 📊 Métricas clave */}
                <div className="space-y-3">
                    <label className="block text-sm font-medium text-gray-700">
                        Métricas clave (2-4 métricas) *
                    </label>
                    
                    {formData.metricas_clave.map((metric, index) => (
                        <div key={index} className="flex gap-2">
                            <input
                                type="text"
                                value={metric}
                                onChange={(e) => handleListChange('metricas_clave', index, e.target.value)}
                                placeholder={`Métrica ${index + 1} (ej: Tasa de conversión, CTR, DAU...)`}
                                className="flex-1 p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                            />
                            {formData.metricas_clave.length > 1 && (
                                <button
                                    onClick={() => removeListItem('metricas_clave', index)}
                                    className="px-3 py-3 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                                >
                                    ✕
                                </button>
                            )}
                        </div>
                    ))}
                    
                    {formData.metricas_clave.length < 4 && (
                        <button
                            onClick={() => addListItem('metricas_clave', 4)}
                            className="text-green-600 hover:text-green-700 text-sm font-medium"
                        >
                            + Agregar métrica clave
                        </button>
                    )}

                    {/* Sugerencias inteligentes de métricas */}
                    <SmartSuggestions
                        hypothesis={sessionData?.hypothesis || ''}
                        mvpDescription={sessionData?.mvp_summary || ''}
                        suggestionType="metricas_clave"
                        sessionId={sessionData?.sessionId || ''}
                        onAddSuggestion={(suggestion) => handleAddSuggestion('metricas_clave', suggestion)}
                    />
                    
                    <div className={`text-xs px-2 py-1 rounded ${
                        validation.metricas_clave.valid 
                            ? 'bg-green-100 text-green-700' 
                            : 'bg-red-100 text-red-700'
                    }`}>
                        {validation.metricas_clave.message}
                    </div>
                </div>

                {/* 🔍 Métodos de medición */}
                <div className="space-y-3">
                    <label className="block text-sm font-medium text-gray-700">
                        Métodos de medición *
                    </label>
                    
                    {formData.metodos_medicion.map((method, index) => (
                        <div key={index} className="flex gap-2">
                            <input
                                type="text"
                                value={method}
                                onChange={(e) => handleListChange('metodos_medicion', index, e.target.value)}
                                placeholder={`Método ${index + 1} (ej: Google Analytics, encuestas, heatmaps...)`}
                                className="flex-1 p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                            />
                            {formData.metodos_medicion.length > 1 && (
                                <button
                                    onClick={() => removeListItem('metodos_medicion', index)}
                                    className="px-3 py-3 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                                >
                                    ✕
                                </button>
                            )}
                        </div>
                    ))}
                    
                    {formData.metodos_medicion.length < 5 && (
                        <button
                            onClick={() => addListItem('metodos_medicion', 5)}
                            className="text-green-600 hover:text-green-700 text-sm font-medium"
                        >
                            + Agregar método de medición
                        </button>
                    )}

                    {/* Sugerencias inteligentes de métodos */}
                    <SmartSuggestions
                        hypothesis={sessionData?.hypothesis || ''}
                        mvpDescription={sessionData?.mvp_summary || ''}
                        suggestionType="metodos_medicion"
                        sessionId={sessionData?.sessionId || ''}
                        onAddSuggestion={(suggestion) => handleAddSuggestion('metodos_medicion', suggestion)}
                    />
                    
                    <div className={`text-xs px-2 py-1 rounded ${
                        validation.metodos_medicion.valid 
                            ? 'bg-green-100 text-green-700' 
                            : 'bg-amber-100 text-amber-700'
                    }`}>
                        {validation.metodos_medicion.message}
                    </div>
                </div>

                {/* 🎯 Objetivos específicos */}
                <div className="space-y-3">
                    <label className="block text-sm font-medium text-gray-700">
                        Objetivos específicos *
                    </label>
                    <div className="relative">
                        <Target className="absolute left-3 top-4 w-5 h-5 text-gray-400" />
                        <textarea
                            value={formData.objetivos_especificos}
                            onChange={(e) => setFormData(prev => ({ ...prev, objetivos_especificos: e.target.value }))}
                            placeholder="Ej: 100 registros en 2 semanas, conversión >5%, tiempo promedio en sitio >2 minutos"
                            className="w-full pl-10 pr-4 py-3 min-h-[80px] border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 resize-none"
                        />
                    </div>
                    <div className={`text-xs px-2 py-1 rounded ${
                        validation.objetivos_especificos.valid 
                            ? 'bg-green-100 text-green-700' 
                            : 'bg-amber-100 text-amber-700'
                    }`}>
                        {validation.objetivos_especificos.message}
                    </div>
                </div>

                {/* 🤖 Botón Validar IA */}
                <div className="flex justify-center pt-4">
                    <button
                        onClick={handleValidateIA}
                        disabled={!isFormComplete}
                        className={`px-8 py-3 rounded-lg font-medium flex items-center gap-2 transition-all ${
                            isFormComplete
                                ? 'bg-gradient-to-r from-purple-500 to-purple-600 text-white hover:shadow-lg transform hover:-translate-y-0.5'
                                : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                        }`}
                    >
                        {isFormComplete ? (
                            <>
                                <TrendingUp className="w-5 h-5" />
                                Validar Métricas con IA
                            </>
                        ) : (
                            <>
                                <AlertCircle className="w-5 h-5" />
                                Completa todos los campos
                            </>
                        )}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default BMLMeasureStep;